import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, KeyRound, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { COMPANY } from "../constants/company";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function StaffPortal() {
  const navigate = useNavigate();
  const { login, setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("token");
  const resetToken = searchParams.get("reset");
  const [view, setView] = useState("login");
  const [resetSent, setResetSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      if (user.role === "client") {
        toast.error("This is the team portal. Please use the client login.");
        return;
      }
      toast.success(`Welcome back, ${user.name.split(" ")[0]}`);
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", {
        token: inviteToken, name: form.name, password: form.password,
      });
      setUser(data);
      toast.success("Account set up — welcome to the team!");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: form.email });
      setResetSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, password: form.password });
      toast.success("Password updated — sign in with your new password");
      navigate("/portal");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary flex" data-testid="staff-portal-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src="/team-office.png" alt="Laronta Pro Tax Services team" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/75" />
        <div className="relative z-10 p-12 flex flex-col justify-between text-primary-foreground">
          <Link to="/" data-testid="portal-logo-link" className="flex items-center">
            <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-16 w-auto rounded-md bg-white/95 p-1.5" />
          </Link>
          <div>
            <div className="flex items-center gap-2 text-secondary text-xs tracking-[0.25em] uppercase font-medium">
              <ShieldCheck className="w-4 h-4" /> Restricted Access
            </div>
            <h1 className="mt-4 text-4xl lg:text-5xl font-heading font-semibold tracking-tight leading-tight">
              Team & Admin Portal
            </h1>
            <p className="mt-4 text-primary-foreground/80 max-w-md">
              Manage your clients, review intakes and documents, and control your calendar — all from one dashboard.
            </p>
          </div>
          <p className="text-sm text-primary-foreground/60">{COMPANY.fullAddress} · {COMPANY.phone}</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md animate-fade-up">
          <Link to="/" data-testid="portal-home-link" className="lg:hidden flex items-center mb-8">
            <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-12 w-auto rounded-md" />
          </Link>

          {resetToken ? (
            <>
              <h2 className="text-3xl font-heading font-semibold tracking-tight" data-testid="portal-reset-heading">Reset Your Password</h2>
              <p className="text-muted-foreground mt-1 text-sm">Choose a new password for your team portal account.</p>
              <form onSubmit={handleReset} className="space-y-4 mt-6" data-testid="portal-reset-form">
                <div className="space-y-1.5">
                  <Label htmlFor="reset-password">New password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input id="reset-password" data-testid="portal-reset-password-input" type="password" required minLength={6} className="pl-9" placeholder="Min. 6 characters" value={form.password} onChange={set("password")} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reset-confirm">Confirm new password</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input id="reset-confirm" data-testid="portal-reset-confirm-input" type="password" required minLength={6} className="pl-9" placeholder="Repeat password" value={form.confirm} onChange={set("confirm")} />
                  </div>
                </div>
                <Button data-testid="portal-reset-submit" type="submit" disabled={loading} className="w-full">
                  {loading ? "Updating…" : "Update Password"}
                </Button>
              </form>
            </>
          ) : view === "forgot" ? (
            <>
              <h2 className="text-3xl font-heading font-semibold tracking-tight" data-testid="portal-forgot-heading">Forgot Password</h2>
              <p className="text-muted-foreground mt-1 text-sm">Enter your work email and we'll send you a branded reset link (expires in 1 hour).</p>
              {resetSent ? (
                <div className="mt-6 bg-muted/50 border border-border rounded-md p-5 text-sm" data-testid="portal-reset-sent">
                  <p className="font-medium text-foreground">Check your inbox</p>
                  <p className="mt-1 text-muted-foreground">If an account exists for {form.email}, a password reset link from {COMPANY.shortName} is on its way.</p>
                  <Button variant="ghost" className="mt-3 px-0" onClick={() => { setView("login"); setResetSent(false); }} data-testid="portal-back-to-login">
                    ← Back to sign in
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-4 mt-6" data-testid="portal-forgot-form">
                  <div className="space-y-1.5">
                    <Label htmlFor="forgot-email">Work email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                      <Input id="forgot-email" data-testid="portal-forgot-email-input" type="email" required className="pl-9" placeholder="you@larontaprotaxservices.com" value={form.email} onChange={set("email")} />
                    </div>
                  </div>
                  <Button data-testid="portal-forgot-submit" type="submit" disabled={loading} className="w-full">
                    {loading ? "Sending…" : "Send Reset Link"}
                  </Button>
                  <Button variant="ghost" className="w-full" type="button" onClick={() => setView("login")} data-testid="portal-forgot-back">
                    ← Back to sign in
                  </Button>
                </form>
              )}
            </>
          ) : inviteToken ? (
            <>
              <h2 className="text-3xl font-heading font-semibold tracking-tight" data-testid="portal-setup-heading">Set Up Your Account</h2>
              <p className="text-muted-foreground mt-1 text-sm">You've been invited to join the {COMPANY.shortName} team. Choose a password to activate your access.</p>
              <form onSubmit={handleSetup} className="space-y-4 mt-6" data-testid="portal-setup-form">
                <div className="space-y-1.5">
                  <Label htmlFor="setup-name">Full name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input id="setup-name" data-testid="portal-setup-name-input" required className="pl-9" placeholder="Jane Doe" value={form.name} onChange={set("name")} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="setup-password">Create password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input id="setup-password" data-testid="portal-setup-password-input" type="password" required minLength={6} className="pl-9" placeholder="Min. 6 characters" value={form.password} onChange={set("password")} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="setup-confirm">Confirm password</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input id="setup-confirm" data-testid="portal-setup-confirm-input" type="password" required minLength={6} className="pl-9" placeholder="Repeat password" value={form.confirm} onChange={set("confirm")} />
                  </div>
                </div>
                <Button data-testid="portal-setup-submit" type="submit" disabled={loading} className="w-full">
                  {loading ? "Activating…" : "Activate My Account"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-heading font-semibold tracking-tight" data-testid="portal-heading">Team & Admin Portal</h2>
              <p className="text-muted-foreground mt-1 text-sm">Authorized staff of {COMPANY.name} only.</p>
              <form onSubmit={handleLogin} className="space-y-4 mt-6" data-testid="portal-login-form">
                <div className="space-y-1.5">
                  <Label htmlFor="portal-email">Work email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input id="portal-email" data-testid="portal-email-input" type="email" required className="pl-9" placeholder="you@larontaprotaxservices.com" value={form.email} onChange={set("email")} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="portal-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input id="portal-password" data-testid="portal-password-input" type="password" required className="pl-9" placeholder="••••••••" value={form.password} onChange={set("password")} />
                  </div>
                </div>
                <Button data-testid="portal-login-submit" type="submit" disabled={loading} className="w-full">
                  {loading ? "Signing in…" : "Sign in to Portal"}
                </Button>
                <button type="button" onClick={() => setView("forgot")} data-testid="portal-forgot-link"
                  className="w-full text-center text-xs text-primary hover:underline underline-offset-2 transition-colors">
                  Forgot your password?
                </button>
              </form>
              <div className="mt-6 bg-muted/50 border border-border rounded-md p-4 text-xs text-muted-foreground" data-testid="portal-invite-note">
                <p className="font-medium text-foreground">New team member?</p>
                <p className="mt-1">Access is by invitation only. Ask your admin to send you an email invitation, then use the link in that email to set up your account.</p>
              </div>
            </>
          )}

          <p className="text-xs text-muted-foreground mt-6 text-center">
            Are you a client? <Link to="/auth" data-testid="portal-client-login-link" className="text-primary underline underline-offset-2">Use the client login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
